import { FlexAligner, Typography } from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';

import { useForceOverlaySideBarOpen } from '@/components/layoutAndProvidersWrapper/sitecontrolLayout/baseLayout';
import { SelectModalContent } from '@/components/layoutAndProvidersWrapper/sitecontrolLayout/baseLayout/siteSelector/selectSiteModal/SelectModalContent.tsx';
import { serviceRequiresSiteConnection, type SiteServiceName } from '@/constants';
import { useSiteServiceListLabel } from '@/hooks';
import { useSiteContext } from '@/providers';
import { useSelectedSiteStore } from '@/stores';
import { responsive } from '@/utils';

type SitePickerForServiceProps = {
  /** Services the picker is for. Pass a stable (module-scope) array — it keys the grouping memos. */
  services: readonly SiteServiceName[];
  /** Hide the active site from the unsupported group while explaining why this page cannot load. */
  hideSelectedUnsupportedSite?: boolean;
  showUnReachableSites?: boolean;
};

export const SitePickerForService = ({
  services,
  hideSelectedUnsupportedSite = false,
  showUnReachableSites = false,
}: SitePickerForServiceProps) => {
  const { t } = useTranslation();
  const setSelectedSiteId = useSelectedSiteStore(s => s.setSelectedSiteId);
  const { currentSite, health } = useSiteContext();
  const serviceList = useSiteServiceListLabel(services);
  useForceOverlaySideBarOpen();

  // A service that reads cloud data only loads on an unreachable site, so blaming the connection
  // there would hide the real reason: the site has none of the devices asked for.
  const isSiteOffline = health === 'unhealthy' && services.some(serviceRequiresSiteConnection);

  return (
    <FlexAligner alignment='center-left' direction={'column'} gap={responsive.spacing['2xlarge']}>
      <Typography type='heading_medium'>
        {isSiteOffline
          ? t('siteOfflineCannotLoadService', { siteName: currentSite?.name })
          : t('siteDoesNotHaveService', {
              siteName: currentSite?.name,
              service: serviceList,
            })}
      </Typography>
      <SelectModalContent
        onSelect={setSelectedSiteId}
        services={services}
        hideSelectedUnsupportedSite={hideSelectedUnsupportedSite}
        showUnReachableSites={showUnReachableSites}
      />
    </FlexAligner>
  );
};
